import { MouseEvent } from 'react';
import AddIcon from '@mui/icons-material/Add';
import Avatar from '@mui/material/Avatar';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';

/**
 * AddUserMenuItem component displays an optional menu item for manually adding new users.
 * Used in autocomplete suggestion lists to allow users to add users that don't exist in the search results.
 *
 * @param props - Component props
 * @param props.isAddCreditOption - Whether to show the "Add New" menu item
 * @param props.isHighlighted - Whether the menu item is currently highlighted
 * @param props.handleManualAddNew - Callback function called when "Add New" is clicked
 * @returns AddUserMenuItem component or null if isAddCreditOption is false
 */
const AddUserMenuItem = (props: AddUserMenuItemPropTypes) => {
  const { isAddCreditOption, isHighlighted, handleManualAddNew } = props;

  if (!isAddCreditOption) {
    return null;
  }

  return (
    <MenuItem
      selected={isHighlighted}
      onClick={handleManualAddNew}
      onMouseDown={(e: MouseEvent) => e.preventDefault()}
      component="div"
      aria-label="Add new item manually"
    >
      <ListItemAvatar>
        <Avatar variant="rounded" sx={{ width: 32, height: 32, bgcolor: 'action.hover' }}>
          <AddIcon fontSize="small" color="action" />
        </Avatar>
      </ListItemAvatar>
      <ListItemText>Add New</ListItemText>
    </MenuItem>
  );
};

export interface AddUserMenuItemPropTypes {
  /** Whether to show the "Add New" menu item */
  isAddCreditOption?: boolean;
  /** Whether the menu item is currently highlighted */
  isHighlighted: boolean;
  /** Callback function called when "Add New" is clicked */
  handleManualAddNew: () => void;
}

export default AddUserMenuItem;
